/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of WasteClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  SuccessResponse_1OfWasteCheckSheetResponse,
  SuccessResponse_1OfPagedData_1OfWasteHeaders,
  SuccessResponse_1OfInt32,
  WasteCheckSheetRequest,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function successResponse<T>(data: T): { data: T; success: boolean; message: string } {
  return { data, success: true, message: 'Success' };
}

/* ---- Mock Data ---- */

const mockWasteCheckSheets = [
  {
    id: 1,
    storeNumber: 'VAP001',
    date: '2026-01-05',
    type: 1,
    status: 'Completed',
    totalItems: 4,
    totalValue: 38.40,
    submittedBy: 'marco.rossi@vapiano.com',
  },
  {
    id: 2,
    storeNumber: 'VAP001',
    date: '2026-01-04',
    type: 1,
    status: 'Completed',
    totalItems: 2,
    totalValue: 5.40,
    submittedBy: 'marco.rossi@vapiano.com',
  },
  {
    id: 3,
    storeNumber: 'FG001',
    date: '2026-01-05',
    type: 1,
    status: 'Completed',
    totalItems: 3,
    totalValue: 68.00,
    submittedBy: 'dave.martinez@fiveguys.com',
  },
  {
    id: 4,
    storeNumber: 'FG001',
    date: '2026-01-04',
    type: 2,
    status: 'Draft',
    totalItems: 0,
    totalValue: 0,
    submittedBy: '',
  },
  {
    id: 5,
    storeNumber: 'VAP002',
    date: '2026-01-05',
    type: 1,
    status: 'Draft',
    totalItems: 0,
    totalValue: 0,
    submittedBy: '',
  },
];

const wasteItemsByStore: Record<string, any[]> = {
  VAP: [
    { itemNumber: 'VAP-MOZZ-001', description: 'Fior di Latte Mozzarella 1kg', quantity: 2, reason: 'Expired', value: 25.60 },
    { itemNumber: 'VAP-BASIL-001', description: 'Fresh Basil (Living Pot)', quantity: 3, reason: 'Damaged', value: 5.40 },
    { itemNumber: 'VAP-PRAWNS-001', description: 'King Prawns (Raw, Peeled) 1kg', quantity: 0.5, reason: 'Expired', value: 9.00 },
    { itemNumber: 'VAP-TOMATO-001', description: 'San Marzano Tomatoes 2.5kg', quantity: 1, reason: 'Damaged', value: 4.80 },
  ],
  FG: [
    { itemNumber: 'FG-BUNS-001', description: 'Sesame Seed Burger Buns x60', quantity: 1, reason: 'Damaged', value: 18.00 },
    { itemNumber: 'FG-PEANUT-OIL-001', description: '100% Refined Peanut Oil 20L', quantity: 1, reason: 'Spillage', value: 32.00 },
    { itemNumber: 'FG-LETTUCE-001', description: 'Iceberg Lettuce (whole heads) x12', quantity: 1, reason: 'Expired', value: 9.60 },
  ],
  DEFAULT: [
    { itemNumber: 'SKU-MILK-001', description: 'Whole Milk 2L', quantity: 2, reason: 'Expired', value: 5.00 },
    { itemNumber: 'SKU-BREAD-001', description: 'White Sliced Bread 800g', quantity: 3, reason: 'Damaged', value: 3.60 },
  ],
};

/* ---- Mocked Client Implementation ---- */

import { IWasteClient } from '../../interfaces';
export class WasteClientMocked implements IWasteClient {
  constructor(baseUrl?: string) {}

  async wasteGET(id: number, cancelToken?: CancelToken): Promise<SuccessResponse_1OfWasteCheckSheetResponse> {
    await delay();
    const sheet = mockWasteCheckSheets.find((w) => w.id === id) || mockWasteCheckSheets[0];
    const storeNumber = sheet.storeNumber || '';
    let items: any[];
    if (storeNumber.startsWith('VAP')) items = wasteItemsByStore['VAP'];
    else if (storeNumber.startsWith('FG')) items = wasteItemsByStore['FG'];
    else items = wasteItemsByStore['DEFAULT'];
    return successResponse({
      ...sheet,
      items,
    }) as SuccessResponse_1OfWasteCheckSheetResponse;
  }

  async wasteDELETE(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockWasteCheckSheets.findIndex((w) => w.id === id);
    if (index > -1) {
      mockWasteCheckSheets.splice(index, 1);
    }
  }

  async create2(body?: WasteCheckSheetRequest, cancelToken?: CancelToken): Promise<SuccessResponse_1OfInt32> {
    await delay();
    const newId = mockWasteCheckSheets.length + 1;
    mockWasteCheckSheets.push({
      id: newId,
      storeNumber: body?.storeNumber || 'STORE001',
      date: body?.createdDate || new Date().toISOString().split('T')[0],
      type: 1, // 1 = Daily
      status: 'Draft',
      totalItems: body?.items?.length || 0,
      totalValue: 0,
      submittedBy: body?.submittedBy || '',
    });
    return successResponse(newId) as SuccessResponse_1OfInt32;
  }

  async submit2(body?: WasteCheckSheetRequest, cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async wasteGET2(
    storeNumber?: string,
    date?: string,
    skip?: number,
    take?: number,
    type?: number,
    fieldToSort?: string,
    sortDirection?: string,
    cancelToken?: CancelToken
  ): Promise<SuccessResponse_1OfPagedData_1OfWasteHeaders> {
    await delay();
    let filtered = [...mockWasteCheckSheets];

    if (storeNumber) {
      filtered = filtered.filter((w) => w.storeNumber === storeNumber);
    }
    if (date) {
      filtered = filtered.filter((w) => w.date === date);
    }
    if (type) {
      filtered = filtered.filter((w) => w.type === type);
    }

    const start = skip || 0;
    const end = start + (take || 10);

    return successResponse({
      data: filtered.slice(start, end),
      totalCount: filtered.length,
      skip: start,
      take: end - start,
    }) as SuccessResponse_1OfPagedData_1OfWasteHeaders;
  }

  // ====== Auto-generated stub methods ======







}
